import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import './EmailConfirmationPage.css';

/**
 * EmailConfirmationPage - Handles email confirmation callback from Supabase
 *
 * This page is shown when users click the email verification link.
 * It processes the confirmation, updates their status, and provides
 * a simple success message with options to continue.
 */
const EmailConfirmationPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      try {
        console.log('[EmailConfirmationPage] Processing email confirmation...');
        console.log('[EmailConfirmationPage] URL hash:', window.location.hash);

        // Parse the hash fragment from Supabase
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');

        console.log('[EmailConfirmationPage] Parsed params:', {
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!refreshToken,
          type
        });

        if (type === 'signup' || type === 'magiclink' || type === 'email') {
          if (accessToken && refreshToken) {
            // Set the session with the tokens from the URL
            console.log('[EmailConfirmationPage] Setting session with tokens...');
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });

            if (error) {
              console.error('[EmailConfirmationPage] Error setting session:', error);
              throw error;
            }

            console.log('[EmailConfirmationPage] Session set successfully:', data?.user?.email);

            // Update user's email verification status in the database
            if (data?.user) {
              console.log('[EmailConfirmationPage] Updating email verification status...');
              const { error: updateError } = await supabase
                .from('verified_users')
                .update({
                  email_verified: true,
                  email_verified_at: new Date().toISOString(),
                  verification_status: 'email_verified'
                })
                .eq('id', data.user.id);

              if (updateError) {
                console.warn('[EmailConfirmationPage] Could not update verification status:', updateError);
                // Don't throw - this is not critical
              }
            }

            // Mark that we just confirmed email for welcome modal
            sessionStorage.setItem('showWelcomeAfterConfirmation', 'true');
            sessionStorage.setItem('auth:justConfirmed', 'true');

            setStatus('success');
          } else {
            // No tokens but type indicates confirmation - might already be confirmed
            console.log('[EmailConfirmationPage] No tokens found, checking existing session...');
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
              console.log('[EmailConfirmationPage] User already has session');
              setStatus('already-confirmed');
            } else {
              setStatus('success'); // Show success anyway
            }
          }
        } else {
          // Unknown type or no type - show generic success
          setStatus('success');
        }
      } catch (err) {
        console.error('[EmailConfirmationPage] Error:', err);
        setError(err.message || 'There was a problem verifying your email.');
        setStatus('error');
      }
    };

    handleEmailConfirmation();
  }, []);

  const handleContinue = () => {
    // Navigate to services page
    navigate('/community/services', { replace: true });
  };

  const handleInstallPWA = () => {
    // Show PWA install instructions
    alert('To install the app:\n\n1. Click the share button in your browser\n2. Select "Add to Home Screen"\n3. Open the app from your home screen');
  };

  if (status === 'processing') {
    return (
      <div className="confirmation-page">
        <div className="confirmation-card">
          <div className="spinner"></div>
          <h2>Verifying your email...</h2>
          <p>Please wait while we confirm your email address.</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="confirmation-page">
        <div className="confirmation-card error">
          <div className="error-icon">⚠️</div>
          <h2>Verification Issue</h2>
          <p>{error}</p>
          <div className="button-group">
            <button className="primary-button" onClick={handleContinue}>
              Continue to Services
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'already-confirmed') {
    return (
      <div className="confirmation-page">
        <div className="confirmation-card success">
          <div className="success-icon">✓</div>
          <h1>Already Verified</h1>
          <p>Your email is already verified and you're signed in!</p>

          <div className="button-group">
            <button className="primary-button" onClick={handleContinue}>
              Continue to Services
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="confirmation-page">
      <div className="confirmation-card success">
        <div className="success-icon">✓</div>
        <h1>Email Verified Successfully!</h1>
        <p className="subtitle">Thank you for verifying your email address.</p>

        <div className="info-section">
          <h3>You can now:</h3>
          <ul>
            <li>✅ Post and browse community services</li>
            <li>✅ Connect with other community members</li>
            <li>✅ Access all platform features</li>
            <li>✅ Maintain your account indefinitely</li>
          </ul>
        </div>

        <div className="pwa-section">
          <h3>📱 For the best experience:</h3>
          <p>Install our app on your device for quick access:</p>
          <ol>
            <li>Tap the <strong>Share</strong> button in your browser</li>
            <li>Select <strong>"Add to Home Screen"</strong></li>
            <li>Open the app from your home screen</li>
          </ol>
          <button className="secondary-button" onClick={handleInstallPWA}>
            Learn How to Install
          </button>
        </div>

        <div className="button-group">
          <button className="primary-button large" onClick={handleContinue}>
            Continue to Services
          </button>
          <button className="secondary-button" onClick={() => window.close()}>
            Close This Tab
          </button>
        </div>

        <p className="footer-note">
          💡 Tip: Bookmark this site or add it to your home screen for easy access!
        </p>
      </div>
    </div>
  );
};

export default EmailConfirmationPage;